import { useState, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { supabase } from '../../lib/supabase'
import { generateTimeSlots, formatLocalDate } from '../../utils/timeSlots'
import './BlockedSlotsManager.css'

function BlockedSlotsManager() {
  const [blockedSlots, setBlockedSlots] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)

  // Form state
  const [selectedDate, setSelectedDate] = useState(null)
  const [blockType, setBlockType] = useState('single') // 'single', 'multiple', 'fullday'
  const [selectedTime, setSelectedTime] = useState('')
  const [selectedTimes, setSelectedTimes] = useState([])
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Filter out weekends
  const isWeekday = (date) => {
    const day = date.getDay()
    return day !== 0 && day !== 6
  }

  // Fetch blocked slots
  const fetchBlockedSlots = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('blocked_slots')
        .select('*')
        .order('blocked_date', { ascending: true })
        .order('blocked_time', { ascending: true })

      if (error) throw error
      setBlockedSlots(data || [])
    } catch (err) {
      console.error('Error fetching blocked slots:', err)
      setError('Failed to load blocked slots')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBlockedSlots()
  }, [])

  // Handle multiple time selection
  const handleTimeSelection = (timeValue) => {
    if (selectedTimes.includes(timeValue)) {
      setSelectedTimes(selectedTimes.filter(t => t !== timeValue))
    } else {
      setSelectedTimes([...selectedTimes, timeValue])
    }
  }

  // Add blocked slot(s)
  const handleAddBlockedSlot = async (e) => {
    e.preventDefault()

    if (!selectedDate) {
      setError('Please select a date')
      return
    }

    let timesToBlock = []

    if (blockType === 'single') {
      if (!selectedTime) {
        setError('Please select a time slot')
        return
      }
      timesToBlock = [selectedTime]
    } else if (blockType === 'multiple') {
      if (selectedTimes.length === 0) {
        setError('Please select at least one time slot')
        return
      }
      timesToBlock = selectedTimes
    } else if (blockType === 'fullday') {
      timesToBlock = allTimeSlots.map(slot => slot.value)
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const dateString = formatLocalDate(selectedDate)

      // Create array of blocked slot records
      const blockedSlotRecords = timesToBlock.map(time => ({
        blocked_date: dateString,
        blocked_time: time,
        reason: reason || null
      }))

      const { error: insertError } = await supabase
        .from('blocked_slots')
        .insert(blockedSlotRecords)

      if (insertError) throw insertError

      // Reset form
      setSelectedDate(null)
      setBlockType('single')
      setSelectedTime('')
      setSelectedTimes([])
      setReason('')
      setShowAddForm(false)

      // Refresh list
      await fetchBlockedSlots()
    } catch (err) {
      console.error('Error adding blocked slot:', err)
      setError(err.message || 'Failed to block time slot(s)')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Delete blocked slot
  const handleDeleteBlockedSlot = async (id) => {
    if (!window.confirm('Are you sure you want to unblock this time slot?')) {
      return
    }

    try {
      const { error: deleteError } = await supabase
        .from('blocked_slots')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError

      // Refresh list
      await fetchBlockedSlots()
    } catch (err) {
      console.error('Error deleting blocked slot:', err)
      alert('Failed to unblock time slot')
    }
  }

  // Delete all blocked slots for a specific date
  const handleUnblockDay = async (date) => {
    const slotsForDate = blockedSlots.filter(slot => slot.blocked_date === date)

    if (!window.confirm(`Are you sure you want to unblock all ${slotsForDate.length} time slots for ${formatDate(date)}?`)) {
      return
    }

    try {
      const { error: deleteError } = await supabase
        .from('blocked_slots')
        .delete()
        .eq('blocked_date', date)

      if (deleteError) throw deleteError

      // Refresh list
      await fetchBlockedSlots()
    } catch (err) {
      console.error('Error deleting blocked slots:', err)
      alert('Failed to unblock day')
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00')
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  // Format time for display
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours)
    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}:${minutes} ${period}`
  }

  // Group blocked slots by date
  const groupedByDate = blockedSlots.reduce((acc, slot) => {
    if (!acc[slot.blocked_date]) {
      acc[slot.blocked_date] = []
    }
    acc[slot.blocked_date].push(slot)
    return acc
  }, {})

  const allTimeSlots = generateTimeSlots()

  return (
    <div className="blocked-slots-manager">
      <div className="section-header">
        <h2>Blocked Time Slots</h2>
        <button
          className="button button-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Block Time Slots'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {showAddForm && (
        <div className="add-blocked-slot-form">
          <h3>Block Time Slots</h3>
          <form onSubmit={handleAddBlockedSlot}>
            <div className="form-group">
              <label>Date</label>
              <DatePicker
                selected={selectedDate}
                onChange={setSelectedDate}
                minDate={new Date()}
                filterDate={isWeekday}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select date"
                required
              />
            </div>

            <div className="form-group">
              <label>Block Type</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    value="single"
                    checked={blockType === 'single'}
                    onChange={(e) => setBlockType(e.target.value)}
                  />
                  <span>Single Time Slot</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    value="multiple"
                    checked={blockType === 'multiple'}
                    onChange={(e) => setBlockType(e.target.value)}
                  />
                  <span>Multiple Time Slots</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    value="fullday"
                    checked={blockType === 'fullday'}
                    onChange={(e) => setBlockType(e.target.value)}
                  />
                  <span>Full Day (All Slots)</span>
                </label>
              </div>
            </div>

            {blockType === 'single' && (
              <div className="form-group">
                <label>Time Slot</label>
                <select
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  required
                >
                  <option value="">-- Select a time slot --</option>
                  {allTimeSlots.map((slot) => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {blockType === 'multiple' && (
              <div className="form-group">
                <label>Select Time Slots ({selectedTimes.length} selected)</label>
                <div className="checkbox-grid">
                  {allTimeSlots.map((slot) => (
                    <label key={slot.value} className="checkbox-option">
                      <input
                        type="checkbox"
                        checked={selectedTimes.includes(slot.value)}
                        onChange={() => handleTimeSelection(slot.value)}
                      />
                      <span>{slot.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {blockType === 'fullday' && (
              <div className="form-group">
                <div className="info-box">
                  <p>All {allTimeSlots.length} time slots will be blocked for the selected date.</p>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Reason (optional)</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Staff meeting, Holiday, Equipment maintenance"
              />
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="button button-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Blocking...' : `Block ${blockType === 'fullday' ? 'Full Day' : blockType === 'multiple' ? `${selectedTimes.length} Slots` : 'Slot'}`}
              </button>
              <button
                type="button"
                className="button button-secondary"
                onClick={() => {
                  setShowAddForm(false)
                  setError(null)
                  setSelectedTimes([])
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="blocked-slots-list">
        {loading ? (
          <p>Loading blocked slots...</p>
        ) : blockedSlots.length === 0 ? (
          <p className="no-data">No blocked time slots</p>
        ) : (
          <div className="grouped-slots">
            {Object.keys(groupedByDate).sort().map((date) => {
              const slots = groupedByDate[date]
              const isFullDay = slots.length === allTimeSlots.length

              return (
                <div key={date} className="date-group">
                  <div className="date-group-header">
                    <div>
                      <h3>{formatDate(date)}</h3>
                      <p className="slot-count">
                        {isFullDay ? 'Full Day Blocked' : `${slots.length} slot${slots.length !== 1 ? 's' : ''} blocked`}
                      </p>
                    </div>
                    <button
                      className="button button-danger button-small"
                      onClick={() => handleUnblockDay(date)}
                    >
                      Unblock {isFullDay ? 'Day' : 'All'}
                    </button>
                  </div>

                  {!isFullDay && (
                    <div className="slots-grid">
                      {slots.map((slot) => (
                        <div key={slot.id} className="slot-card">
                          <div className="slot-info">
                            <span className="slot-time">{formatTime(slot.blocked_time)}</span>
                            {slot.reason && <span className="slot-reason">{slot.reason}</span>}
                          </div>
                          <button
                            className="button-icon"
                            onClick={() => handleDeleteBlockedSlot(slot.id)}
                            title="Unblock this slot"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {isFullDay && slots[0].reason && (
                    <p className="full-day-reason">Reason: {slots[0].reason}</p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default BlockedSlotsManager
